import cx from 'classnames';
import { Fragment } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../../../../core/Tracking/Amplitude/Amplitude';
import {
    ItineraryCardProps,
    ItineraryCard,
} from '../../../../../../design-system/components/card/itinerary/ItineraryCard';
import { Button } from '../../../../../../design-system/components/form/button/Button';
import { DotsLoader } from '../../../../../../design-system/components/loader/dots/DotsLoader';
import { Agency } from '../../../../../../resources/agency/domain/types/Agency';
import { Destination } from '../../../../../../resources/destination/domain/types/destination';
import { useUrlGenerator } from '../../../../../../shared/hooks/useUrlGenerator';
import { PickDomain } from '../../../../../../shared/utilities/types/PickDomain';

import style from './ItinerariesGrid.module.css';

type ItinerariesGridProps = {
    hasNextPage: boolean;
    isAnyPageLoading: boolean;
    agency: PickDomain<Agency, 'name' | 'id' | 'slug' | 'betterTripIndex'>;
    handleSeeMore: () => void;
    itinerariesCount: number;
    destination: PickDomain<Destination, 'id' | 'slug' | 'name'>;
    itineraries: Array<ItineraryCardProps['itinerary']>;
};

export const ItinerariesGrid = ({
    itineraries,
    destination,
    itinerariesCount,
    handleSeeMore,
    hasNextPage,
    isAnyPageLoading,
    agency,
}: ItinerariesGridProps) => {
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();

    const handleItinerariesListClick = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.itinerariesListClicked({
                ...defaultProperties,
                block: 'itineraries',
                destination_id: destination.id,
                destination_slug: destination.slug,
            });
        });
    };

    return (
        <section className={cx('main-container', style.gridSection)}>
            {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
            <h2 className="font-m-extrabold">
                {formatMessage(
                    {
                        id: 'page.agency.itineraries.title',
                    },
                    { agencyName: agency.name },
                )}
            </h2>
            {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
            <p className={cx('font-body-bold', style.subtitle)}>
                {formatMessage({
                    id: 'page.agency.itineraries.subtitle',
                })}
            </p>
            <section className={style.containerGrid}>
                {itineraries.map((itinerary, index) => {
                    return (
                        <Fragment key={itinerary.id}>
                            <ItineraryCard
                                image={{ sizes: '300px' }}
                                itinerary={itinerary}
                                hideAgency={true}
                                trackingInformation={{
                                    click: {
                                        block: 'itineraries',
                                        card_number: index + 1,
                                        row_number: 1,
                                        tool_counter: 0,
                                    },
                                    wishListAdded: {
                                        block: 'itineraries',
                                    },
                                }}
                            />
                        </Fragment>
                    );
                })}
            </section>
            {itinerariesCount > 0 ? (
                <div className={style.paginationWrapper}>
                    <>
                        {hasNextPage && !isAnyPageLoading ? (
                            <Button priority="secondary" onClick={handleSeeMore}>
                                {formatMessage({
                                    id: 'page.itinerariesList.showMore',
                                })}
                            </Button>
                        ) : null}
                    </>
                    {isAnyPageLoading ? (
                        <div className={style.loaderWrapper}>
                            <DotsLoader />
                        </div>
                    ) : null}
                </div>
            ) : null}

            <div className={style.footerWrapper}>
                <div className={style.itinerariesFooter}>
                    {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                    <p className="font-body-bold">
                        {formatMessage({ id: 'page.agency.itineraries-subtitle' })}
                    </p>
                    <Button
                        priority="text"
                        href={generatePath('itinerary_list', {
                            destinationSlug: destination.slug,
                        })}
                        onClick={handleItinerariesListClick}
                    >
                        {formatMessage(
                            { id: 'page.agency.itineraries.destination' },
                            { destinationName: destination.name },
                        )}
                    </Button>
                </div>
            </div>
        </section>
    );
};
